/**
 * Transaction Item Component
 */
import React, { useState } from 'react';
import { Zap, Tv, Droplets, Copy, Download, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL, BACKEND_URL } from '../config';


const formatUtilityToken = (token) => {
  const digits = String(token || "").replace(/\D/g, "");
  if (digits.length >= 16) {
    return digits.replace(/(\d{4})(?=\d)/g, "$1 ").trim();
  }
  return String(token || "");
};

const TransactionItem = ({ transaction, compact = false, showDownload = false }) => {
  const [downloading, setDownloading] = useState(false);
  const statusColors = { pending: "status-pending", success: "status-success", failed: "status-failed" };
  const displayToken =
    transaction.token && !String(transaction.token).startsWith("enc:")
      ? formatUtilityToken(transaction.token)
      : null;
  const isTaxPayment = transaction.provider_code === "MRA";
  const tokenLabel = isTaxPayment ? "Receipt reference" : "Your token";

  const getIcon = () => {
    if (transaction.provider_code === "ESCOM") return <Zap className="w-5 h-5 text-yellow-500" />;
    if (["DSTV", "GOTV"].includes(transaction.provider_code)) return <Tv className="w-5 h-5 text-blue-500" />;
    return <Droplets className="w-5 h-5 text-cyan-500" />;
  };

  const getIconBg = () => {
    if (transaction.provider_code === "ESCOM") return "bg-yellow-500/20";
    if (["DSTV", "GOTV"].includes(transaction.provider_code)) return "bg-blue-500/20";
    return "bg-cyan-500/20";
  };

  const handleDownloadReceipt = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem("auth_token");
      const response = await axios.get(`${API_URL}/transactions/${transaction.id}/receipt`, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` }
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `receipt_${transaction.reference}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Receipt downloaded!");
    } catch (error) {
      toast.error("Failed to download receipt");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      className={`${compact ? "py-3" : "p-4 bg-card border border-border/50 rounded-xl"} ${
        displayToken ? "space-y-3" : ""
      }`}
    >
      <div className="flex items-center gap-4">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${getIconBg()}`}>
          {getIcon()}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium truncate">{transaction.provider_code}</p>
          {transaction.customer_name && (
            <p className="text-sm truncate">{transaction.customer_name}</p>
          )}
          <p className="text-sm text-muted-foreground truncate">{transaction.account_number}</p>
        </div>
        <div className="text-right shrink-0">
          <p className="font-semibold">MWK {transaction.amount.toLocaleString()}</p>
          <span
            className={`inline-block px-2 py-0.5 text-xs rounded-full ${statusColors[transaction.status] || ""}`}
          >
            {transaction.status}
          </span>
        </div>
        {displayToken && (
          <button
            type="button"
            onClick={() => {
              navigator.clipboard.writeText(transaction.token.replace(/\s/g, ""));
              toast.success("Token copied!");
            }}
            className="p-2 hover:bg-muted rounded-lg transition-all shrink-0"
            title="Copy token"
          >
            <Copy className="w-4 h-4" />
          </button>
        )}
        {(showDownload || !compact) && transaction.status === "success" && (
          <button
            type="button"
            onClick={handleDownloadReceipt}
            disabled={downloading}
            className="p-2 hover:bg-muted rounded-lg transition-all shrink-0"
            title="Download receipt"
            data-testid={`download-receipt-${transaction.id}`}
          >
            {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          </button>
        )}
      </div>

      {displayToken && (
        <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/25 px-3 py-2.5">
          <p className="text-xs font-medium text-emerald-800 dark:text-emerald-300 mb-1">{tokenLabel}</p>
          <p className="font-mono text-sm font-semibold text-emerald-900 dark:text-emerald-100 break-all leading-relaxed">
            {displayToken}
          </p>
          {transaction.token_units != null && (
            <p className="text-xs text-muted-foreground mt-1">Units: {transaction.token_units}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default TransactionItem;